import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Scale, Search, RefreshCw, Download, Eye, AlertTriangle,
  CheckCircle2, FileSpreadsheet, Calendar,
} from "lucide-react";

const currentMonthIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const BUCKET_META = {
  match:    { label: "Match",       cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  minor:    { label: "Minor diff",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
  major:    { label: "MAJOR diff",  cls: "bg-red-50 text-red-700 border-red-300 font-semibold" },
  csv_only: { label: "CSV only",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
  bio_only: { label: "Bio only",    cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

const fmtSigned = (v, suffix = "") => {
  if (v === 0 || v === null || v === undefined) return "0" + suffix;
  const sign = v > 0 ? "+" : "";
  return `${sign}${v}${suffix}`;
};

const diffCls = (v) => {
  if (Math.abs(v) < 0.001) return "text-slate-400";
  return v > 0 ? "text-blue-700 font-semibold" : "text-red-700 font-semibold";
};

export default function PayrollComparison() {
  const [month, setMonth] = useState(currentMonthIso());
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [bucketFilter, setBucketFilter] = useState("all");
  const [drillRow, setDrillRow] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/biometric/payroll-comparison", {
        params: { month },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load comparison");
      setData({ rows: [], summary: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const visibleRows = useMemo(() => {
    let arr = data.rows || [];
    if (bucketFilter !== "all") arr = arr.filter(r => r.bucket === bucketFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(r =>
        (r.employee_name || "").toLowerCase().includes(q) ||
        (r.employee_code || "").toLowerCase().includes(q) ||
        (r.punch_id || "").toLowerCase().includes(q) ||
        (r.biometric_id || "").toLowerCase().includes(q));
    }
    return arr;
  }, [data.rows, bucketFilter, search]);

  const openDrill = async (row) => {
    setDrillRow(row);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const r = await api.get(`/biometric/payroll-comparison/${row.employee_id}`,
                              { params: { month } });
      setDrillData(r.data);
    } catch (e) {
      toast.error("Failed to load drill-down");
    } finally {
      setDrillLoading(false);
    }
  };

  const exportCsv = () => {
    if (!visibleRows.length) return toast.error("Nothing to export");
    const headers = [
      "Employee", "Bravura Code", "Bio ID", "Punch ID", "Status",
      "CSV Days", "Bio Days", "Diff Days",
      "CSV Hours", "Bio Hours", "Diff Hours",
      "CSV OT Hrs", "Bio OT Hrs", "Diff OT Hrs",
    ];
    const lines = [headers];
    visibleRows.forEach(r => lines.push([
      r.employee_name, r.employee_code, r.biometric_id || "", r.punch_id || "",
      BUCKET_META[r.bucket]?.label || r.bucket,
      r.csv.present_days, r.bio.present_days, r.diff.days,
      r.csv.expected_hours, r.bio.total_hours, r.diff.hours,
      r.csv.ot_hours, r.bio.ot_hours, r.diff.ot_hours,
    ]));
    const csv = lines.map(row => row.map(c =>
      `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_comparison_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sm = data.summary || {};

  return (
    <div data-testid="payroll-comparison-page" className="p-6 space-y-5 max-w-[1500px]">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Scale className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 5G-C</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Payroll Comparison
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Side-by-side <b>Paytime CSV</b> vs <b>Biometric punches</b> for every mapped employee.
            {" "}Read-only — payroll authority remains with Paytime CSV.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <Calendar className="h-3.5 w-3.5 text-slate-500" />
            <input type="month" value={month}
                   onChange={(e) => setMonth(e.target.value || currentMonthIso())}
                   className="text-xs bg-transparent outline-none"
                   data-testid="comparison-month" />
          </div>
          <div className="flex items-center gap-1 flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search by name, code, or biometric ID..."
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   className="h-8 text-xs"
                   data-testid="comparison-search" />
          </div>
          <select value={bucketFilter}
                  onChange={(e) => setBucketFilter(e.target.value)}
                  className="h-8 text-xs border border-slate-200 rounded px-2"
                  data-testid="comparison-bucket-filter">
            <option value="all">All ({sm.total || 0})</option>
            <option value="major">Major diff ({sm.major || 0})</option>
            <option value="minor">Minor diff ({sm.minor || 0})</option>
            <option value="csv_only">CSV only ({sm.csv_only || 0})</option>
            <option value="bio_only">Bio only ({sm.bio_only || 0})</option>
            <option value="match">Match ({sm.match || 0})</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  data-testid="comparison-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv}
                  data-testid="comparison-export">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Match" value={sm.match || 0} cls="bg-emerald-50 border-emerald-200 text-emerald-900" testid="card-match" />
        <SummaryCard label="Minor diff" value={sm.minor || 0} cls="bg-amber-50 border-amber-200 text-amber-900" testid="card-minor" />
        <SummaryCard label="Major diff" value={sm.major || 0} cls="bg-red-50 border-red-200 text-red-900" testid="card-major" />
        <SummaryCard label="CSV only" value={sm.csv_only || 0} cls="bg-blue-50 border-blue-200 text-blue-900" testid="card-csv-only" />
        <SummaryCard label="Bio only" value={sm.bio_only || 0} cls="bg-violet-50 border-violet-200 text-violet-900" testid="card-bio-only" />
      </div>

      {/* Comparison Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="comparison-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10">Employee</th>
                <th className="text-center px-2 py-2 w-24">Status</th>
                <th colSpan={3} className="text-center px-2 py-2 bg-blue-50/40 border-l border-blue-200">Paytime CSV</th>
                <th colSpan={3} className="text-center px-2 py-2 bg-violet-50/40 border-l border-violet-200">Biometric</th>
                <th colSpan={3} className="text-center px-2 py-2 bg-amber-50/40 border-l border-amber-200">Difference (Bio − CSV)</th>
                <th className="px-2 py-2 w-12"></th>
              </tr>
              <tr className="text-[9px]">
                <th className="text-left px-3 py-1 sticky left-0 bg-slate-50 z-10"></th>
                <th className=""></th>
                <th className="px-2 py-1 bg-blue-50/40 border-l border-blue-200">Days</th>
                <th className="px-2 py-1 bg-blue-50/40">Hours</th>
                <th className="px-2 py-1 bg-blue-50/40">OT</th>
                <th className="px-2 py-1 bg-violet-50/40 border-l border-violet-200">Days</th>
                <th className="px-2 py-1 bg-violet-50/40">Hours</th>
                <th className="px-2 py-1 bg-violet-50/40">OT</th>
                <th className="px-2 py-1 bg-amber-50/40 border-l border-amber-200">Δ Days</th>
                <th className="px-2 py-1 bg-amber-50/40">Δ Hours</th>
                <th className="px-2 py-1 bg-amber-50/40">Δ OT</th>
                <th className=""></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="12" className="text-center py-10 text-slate-400">Loading…</td></tr>
              )}
              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan="12" className="text-center py-10 text-slate-400">
                  {(data.rows || []).length === 0
                    ? "No mapped employees yet for this month. Map biometric IDs first."
                    : "No rows match current filter."}
                </td></tr>
              )}
              {!loading && visibleRows.map(r => (
                <tr key={r.employee_id}
                    className={`hover:bg-red-50/40 ${r.bucket === "major" ? "bg-red-50/30" : ""}`}
                    data-testid={`comparison-row-${r.employee_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white z-10">
                    <div className="font-semibold text-slate-900 text-xs">{r.employee_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {r.employee_code} · Bio:{r.biometric_id || "—"} · Punch:{r.punch_id || "—"}
                    </div>
                  </td>
                  <td className="text-center px-1">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded border ${BUCKET_META[r.bucket]?.cls || ""}`}>
                      {BUCKET_META[r.bucket]?.label || r.bucket}
                    </span>
                  </td>
                  {/* CSV side */}
                  <td className="px-2 py-2 font-mono text-center bg-blue-50/30 border-l border-blue-200">
                    {r.csv.exists ? r.csv.present_days : "—"}
                  </td>
                  <td className="px-2 py-2 font-mono text-center bg-blue-50/30">
                    {r.csv.exists ? r.csv.expected_hours + "h" : "—"}
                  </td>
                  <td className="px-2 py-2 font-mono text-center bg-blue-50/30">
                    {r.csv.exists ? r.csv.ot_hours + "h" : "—"}
                  </td>
                  {/* Bio side */}
                  <td className="px-2 py-2 font-mono text-center bg-violet-50/30 border-l border-violet-200">
                    {r.bio.present_days}
                  </td>
                  <td className="px-2 py-2 font-mono text-center bg-violet-50/30">
                    {r.bio.total_hours}h
                  </td>
                  <td className="px-2 py-2 font-mono text-center bg-violet-50/30">
                    {r.bio.ot_hours}h
                  </td>
                  {/* Diff */}
                  <td className={`px-2 py-2 font-mono text-center bg-amber-50/30 border-l border-amber-200 ${diffCls(r.diff.days)}`}>
                    {fmtSigned(r.diff.days)}
                  </td>
                  <td className={`px-2 py-2 font-mono text-center bg-amber-50/30 ${diffCls(r.diff.hours)}`}>
                    {fmtSigned(r.diff.hours, "h")}
                  </td>
                  <td className={`px-2 py-2 font-mono text-center bg-amber-50/30 ${diffCls(r.diff.ot_hours)}`}>
                    {fmtSigned(r.diff.ot_hours, "h")}
                  </td>
                  <td className="px-1 text-center">
                    <Button size="sm" variant="outline"
                            className="h-7 px-2"
                            onClick={() => openDrill(r)}
                            data-testid={`drill-btn-${r.employee_id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-[10px] text-slate-400 text-center">
        ⓘ Difference = Biometric − CSV. Positive (blue) = biometric recorded more; Negative (red) = biometric recorded less.
        {" "}Payroll calculation uses CSV as source of truth.
      </p>

      {/* Drill-down dialog */}
      <Dialog open={!!drillRow} onOpenChange={(v) => { if (!v) { setDrillRow(null); setDrillData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-red-600" />
              Daily breakdown — {drillRow?.employee_name}
            </DialogTitle>
            <DialogDescription>
              {drillRow?.employee_code} · Bio: {drillRow?.biometric_id || "—"} · {month}
            </DialogDescription>
          </DialogHeader>
          {drillLoading && <div className="text-center text-sm py-8 text-slate-400">Loading...</div>}
          {drillData && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" data-testid="drilldown-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 bg-blue-50/40 border-l border-blue-200" colSpan={3}>Paytime CSV</th>
                    <th className="px-2 py-2 bg-violet-50/40 border-l border-violet-200" colSpan={4}>Biometric</th>
                    <th className="px-2 py-2 text-center w-12"></th>
                  </tr>
                  <tr className="text-[9px] border-b border-slate-200">
                    <th></th>
                    <th className="px-2 py-1 bg-blue-50/40 border-l border-blue-200">First In</th>
                    <th className="px-2 py-1 bg-blue-50/40">Last Out</th>
                    <th className="px-2 py-1 bg-blue-50/40">Grade</th>
                    <th className="px-2 py-1 bg-violet-50/40 border-l border-violet-200">First In</th>
                    <th className="px-2 py-1 bg-violet-50/40">Last Out</th>
                    <th className="px-2 py-1 bg-violet-50/40">Punches</th>
                    <th className="px-2 py-1 bg-violet-50/40">Grade</th>
                    <th className="text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {drillData.days.map(d => {
                    const bothEmpty = !d.csv.exists && !d.bio.exists;
                    const onlyOne = (d.csv.exists !== d.bio.exists);
                    return (
                      <tr key={d.date}
                          className={`${d.is_sunday ? "bg-amber-50/40" : ""} ${onlyOne ? "bg-red-50/20" : ""}`}>
                        <td className="px-2 py-1.5 font-mono text-[11px]">
                          <div>{d.date}</div>
                          <div className={`text-[9px] uppercase ${d.is_sunday ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{d.weekday}</div>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-center bg-blue-50/20 border-l border-blue-200">
                          {d.csv.first_in || (bothEmpty ? "—" : (d.csv.exists ? "—" : "not in CSV"))}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-center bg-blue-50/20">
                          {d.csv.last_out || ""}
                        </td>
                        <td className="px-2 py-1.5 text-center bg-blue-50/20 text-[10px]">
                          {d.csv.grade || "—"}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-center bg-violet-50/20 border-l border-violet-200">
                          {d.bio.first_in || (bothEmpty ? "—" : (d.bio.exists ? "—" : "no punch"))}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-center bg-violet-50/20">
                          {d.bio.first_in === d.bio.last_out ? "" : (d.bio.last_out || "")}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-center bg-violet-50/20 text-[10px]">
                          {d.bio.punch_count || "—"}
                        </td>
                        <td className="px-2 py-1.5 text-center bg-violet-50/20 text-[10px]">
                          {d.bio.grade}
                        </td>
                        <td className="text-center">
                          {onlyOne ? (
                            <AlertTriangle className="h-3.5 w-3.5 text-red-600 inline" />
                          ) : !bothEmpty ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 inline" />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, cls, testid }) {
  return (
    <div className={`border rounded-lg px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1" style={{ fontFamily: "Chivo" }}>{value}</div>
    </div>
  );
}
